import { useState } from 'react';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import { functions } from '../firebase';
import { useGameStore } from '../store/useGameStore';

const requestEssenceCallable = httpsCallable<
  unknown,
  { ok: boolean; essence: number; granted: number }
>(functions, 'requestEssence');

// Header chip showing the spirit's per-player essence budget. Click to open a
// refill dialog. The pool is decremented server-side by 1 per whisper plus the
// 1 essence cost for committing a road.
export function EssenceChip() {
  const essence = useGameStore((s) => s.user?.essence ?? 0);
  const updateEssence = useGameStore((s) => s.updateEssence);
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Spend essence to whisper and commit actions. Click to request more."
        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs leading-none transition-colors ${
          essence <= 0
            ? 'border-rose-500/60 bg-rose-950/30 text-rose-200 hover:bg-rose-900/40'
            : 'border-amber-400/40 bg-amber-950/30 text-amber-100 hover:bg-amber-900/40'
        }`}
      >
        <span aria-hidden>✦</span>
        <span>essence</span>
        <span className="tabular-nums">{essence}</span>
      </button>
      {open && (
        <RefillDialog
          essence={essence}
          onClose={() => setOpen(false)}
          onGranted={(next) => {
            updateEssence(next);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

function RefillDialog({
  essence,
  onClose,
  onGranted,
}: {
  essence: number;
  onClose: () => void;
  onGranted: (next: number) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refill() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await requestEssenceCallable({});
      onGranted(res.data.essence);
    } catch (e) {
      const fe = e as FunctionsError;
      setError(fe?.message ?? 'no essence came');
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-xl"
      >
        <h2 className="text-lg text-slate-100">
          {essence <= 0 ? 'Out of essence' : 'Essence'}
        </h2>
        <p className="mt-1.5 text-sm text-slate-400">
          Essence is your power to whisper and to steer. Every whisper costs 1, and committing a
          character to a road costs 1. You have{' '}
          <span className="text-amber-200/90 tabular-nums">{essence}</span> right now.
        </p>
        <p className="mt-2 text-sm text-slate-400">
          For now you can request 10 more for free.
        </p>
        {error && <p className="mt-2 text-xs text-rose-400">{error}</p>}
        <div className="mt-5 flex justify-end gap-3 text-sm">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg border border-slate-700 text-slate-400 hover:text-slate-200"
          >
            close
          </button>
          <button
            type="button"
            onClick={refill}
            disabled={busy}
            className="px-4 py-1.5 rounded-lg border border-amber-400/60 text-amber-200 bg-amber-900/30 hover:bg-amber-900/50 disabled:opacity-50"
          >
            {busy ? 'breathing in…' : '+10 essence (free)'}
          </button>
        </div>
      </div>
    </div>
  );
}
